const CHART_DESCRIPTIONS = {
  wer_buckets:
    "Distribution of per-row WER across bands (e.g. 0-10%, 10-20%…). Tall green bars mean most rows transcribed well; tall red bars mean many high-error rows.",
  wer_by_age_tier:
    "Mean WER grouped by speaker age tier. Helps see if the model performs worse for specific age groups (e.g. children vs adults).",
  wer_by_label:
    "Mean WER grouped by dataset label. Useful for spotting which content types are hardest for the STT model.",
  top_tokens:
    "Most frequent words/tokens in the reference transcripts. Indicates the dominant vocabulary of the dataset.",
  rarest_words:
    "Least frequent words in the reference transcripts (long-tail vocabulary). These are often where the model struggles most.",
  error_type_breakdown:
    "Breakdown of WER errors into Substitutions, Insertions, and Deletions. Reveals what kind of mistakes the model makes most.",
  zipf_loglog:
    "Log-log plot of word rank vs frequency. A straight line (slope ≈ -1) indicates a natural-language distribution (Zipf's law).",
  duration_hist:
    "Histogram of audio clip durations in seconds. Use this to check the dataset isn't biased toward very short or very long clips.",
  wer_vs_duration:
    "Scatter of per-row WER against clip duration. Used to check whether longer/shorter clips correlate with higher error rates.",
  wer_vs_snr:
    "Scatter of per-row WER against signal-to-noise ratio. A negative correlation means noisier audio hurts accuracy.",
  text_outliers:
    "Transcripts that DBSCAN labelled as noise (cluster -1) after TF-IDF (char 2-4) + SVD reduction. High % means heterogeneous transcripts.",
};

const METRIC_DESCRIPTIONS = {
  wer_mean: "Average Word Error Rate across all rows. Lower is better.",
  wer_median: "Median Word Error Rate. Less sensitive to outliers than the mean.",
  wer_p90: "90th-percentile WER — 90% of rows have a WER at or below this value.",
  wer_p95: "95th-percentile WER — useful for tracking worst-case behavior.",
  wer_n_rows: "Number of rows included in the WER computation.",
  cer_mean: "Average Character Error Rate. Like WER but at the character level — better for languages without clear word boundaries.",
  cer_median: "Median Character Error Rate.",
  sub_rate: "Substitution rate — fraction of reference words replaced by a different word.",
  ins_rate: "Insertion rate — fraction of words added by the model that weren't in the reference.",
  del_rate: "Deletion rate — fraction of reference words missed by the model.",
  outlier_pct:
    "Percentage of transcripts classified as outliers by DBSCAN. Higher means the dataset is more heterogeneous.",
  duration_mean_sec: "Average clip duration in seconds.",
  duration_median_sec: "Median clip duration in seconds.",
  duration_total_hours: "Total audio in the dataset, in hours.",
  unique_tokens: "Count of distinct word tokens across all reference transcripts (vocabulary size).",
  total_tokens: "Total word count across all reference transcripts.",
  type_token_ratio: "unique_tokens / total_tokens. Higher means richer vocabulary; lower means more repetition.",
};

export function getMetricInfo(metric) {
  if (!metric) return null;
  if (metric.description) return metric.description;
  return METRIC_DESCRIPTIONS[metric.id] || null;
}

export function getChartInfo(chart) {
  if (!chart) return null;
  if (chart.description) return chart.description;
  return CHART_DESCRIPTIONS[chart.id] || null;
}

export function InfoIcon({ text }) {
  if (!text) return null;
  return (
    <span className="eval-info-icon" tabIndex={0} aria-label={text}>
      <span aria-hidden="true">i</span>
      <span className="eval-info-tooltip" role="tooltip">{text}</span>
    </span>
  );
}

export function WithTooltip({ text, children, className = "", as: As = "span" }) {
  if (!text) return <As className={className}>{children}</As>;
  return (
    <As className={`eval-tt-wrapper ${className}`} tabIndex={0}>
      {children}
      <span className="eval-tt-content" role="tooltip">{text}</span>
    </As>
  );
}

export function formatPercent(value, digits = 1) {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  return `${(value * 100).toFixed(digits)}%`;
}

export function formatCount(value) {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  return Number(value).toLocaleString();
}

export function werColor(v) {
  if (v === null || v === undefined || Number.isNaN(v)) return "var(--bg-slate)";
  const clamped = Math.max(0, Math.min(1, Number(v)));
  const hue = 120 * (1 - clamped); // 120 = green, 0 = red
  return `hsl(${hue}, 65%, 42%)`;
}

export function buildCellTooltip({ rowDesc, colDesc, label, value, valueLabel = "Value" }) {
  const parts = [];
  if (rowDesc) parts.push(rowDesc);
  if (colDesc) parts.push("×", colDesc);
  parts.push("→", `${label || valueLabel}: ${value}`);
  return parts.join("\n");
}
